import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Time helpers for the tracker. JSON instants are UTC (A03) and every day is
 * an Asia/Kolkata day. The browser's clock and time zone are never used to
 * decide what "today" is (U14); that comes from the server's `as_of`.
 */
export const IST_ZONE = 'Asia/Kolkata';
export const DAY_FORMAT = 'YYYY-MM-DD';
export const MAX_RANGE_DAYS = 366;
export const DEFAULT_RANGE_DAYS = 30;
export const RANGE_PRESETS = Object.freeze([7, 30, 90]);

const RELATIVE_LIMIT_DAYS = 7;
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Is this a parseable instant or day? */
const isValidInput = (value) =>
  value !== null && value !== undefined && value !== '' && dayjs(value).isValid();

/**
 * A UTC instant as a dayjs object in IST.
 * @param {string|number|Date} instant
 * @returns {import('dayjs').Dayjs|null}
 */
export function toIst(instant) {
  if (!isValidInput(instant)) return null;
  return dayjs.utc(instant).tz(IST_ZONE);
}

/**
 * The IST calendar day of a UTC instant: `2026-10-19T20:00Z` → `2026-10-20`.
 * @param {string|number|Date} instant
 * @returns {string|null}
 */
export function istDayOf(instant) {
  const ist = toIst(instant);
  return ist ? ist.format(DAY_FORMAT) : null;
}

/** "22 Oct, 19:33 IST" — lists. */
export function formatIstShort(instant) {
  const ist = toIst(instant);
  return ist ? `${ist.format('D MMM, HH:mm')} IST` : '';
}

/** "22 Oct 2026, 19:33:11" — hit log. */
export function formatIstLog(instant) {
  const ist = toIst(instant);
  return ist ? ist.format('D MMM YYYY, HH:mm:ss') : '';
}

/** "12 Nov 2026, 18:00 IST" — expiry and history headings. */
export function formatIstLong(instant) {
  const ist = toIst(instant);
  return ist ? `${ist.format('D MMM YYYY, HH:mm')} IST` : '';
}

/** "3 Oct" for an IST day string or instant. */
export function formatIstDayShort(dayOrInstant) {
  const ist = toIst(dayOrInstant);
  return ist ? ist.format('D MMM') : '';
}

/**
 * "24 Sep – 23 Oct 2026" for a pair of IST day strings (no TZ conversion:
 * a day string is already an IST day).
 */
export function formatDayRange(from, to) {
  if (!from || !to) return '';
  const a = dayjs(from, DAY_FORMAT);
  const b = dayjs(to, DAY_FORMAT);
  const left = a.year() === b.year() ? a.format('D MMM') : a.format('D MMM YYYY');
  return `${left} – ${b.format('D MMM YYYY')}`;
}

/**
 * Relative time for the Links table last-hit cell, only under 7 days (U71).
 * Returns null at 7 days or more, so the caller shows the IST day instead.
 * @param {string} instant
 * @param {number} [nowMs=Date.now()]
 * @returns {string|null}
 */
export function relativeUnderAWeek(instant, nowMs = Date.now()) {
  if (!isValidInput(instant)) return null;
  const diff = Math.max(0, nowMs - dayjs.utc(instant).valueOf());
  if (diff >= RELATIVE_LIMIT_DAYS * DAY_MS) return null;
  if (diff < HOUR_MS) return `${Math.max(1, Math.floor(diff / MINUTE_MS))} min ago`;
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)} h ago`;
  const days = Math.floor(diff / DAY_MS);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

/**
 * Shift an IST day string by N days (pure calendar arithmetic).
 * @param {string} day
 * @param {number} n
 * @returns {string}
 */
export function addDays(day, n) {
  return dayjs(day, DAY_FORMAT).add(n, 'day').format(DAY_FORMAT);
}

/** Inclusive day count between two IST day strings. */
export function spanDays(from, to) {
  return dayjs(to, DAY_FORMAT).diff(dayjs(from, DAY_FORMAT), 'day') + 1;
}

/**
 * "Last N days" from the server's today: `todayIst−(N−1) … todayIst` (U14).
 * @param {string} todayIst
 * @param {number} n
 * @returns {{ from: string, to: string }}
 */
export function presetRange(todayIst, n) {
  return { from: addDays(todayIst, -(n - 1)), to: todayIst };
}

/**
 * Which preset (7/30/90) a range equals relative to today, or null (custom).
 * @param {{ from?: string|null, to?: string|null }} range
 * @param {string|null} todayIst
 * @returns {number|null}
 */
export function matchPreset(range, todayIst) {
  if (!todayIst || !range?.from || !range?.to) return null;
  const hit = RANGE_PRESETS.find((n) => {
    const p = presetRange(todayIst, n);
    return p.from === range.from && p.to === range.to;
  });
  return hit ?? null;
}

/**
 * Validate a custom range (U15). Returns an error key, or null when valid.
 * @param {string} from
 * @param {string} to
 * @param {string|null} todayIst
 * @returns {'invalid'|'too_long'|'future'|null}
 */
export function validateRange(from, to, todayIst) {
  if (!from || !to || !dayjs(from).isValid() || !dayjs(to).isValid()) return 'invalid';
  if (to < from) return 'invalid';
  if (todayIst && to > todayIst) return 'future';
  if (spanDays(from, to) > MAX_RANGE_DAYS) return 'too_long';
  return null;
}

/**
 * Convert a wall-clock IST date-time (as picked) to the UTC ISO instant the
 * API expects, whatever the device time zone (U39).
 * @param {string} istLocal - "YYYY-MM-DDTHH:mm" or "YYYY-MM-DD HH:mm" in IST.
 * @returns {string|null}
 */
export function istLocalToUtcIso(istLocal) {
  if (!istLocal) return null;
  const parsed = dayjs.tz(String(istLocal).replace(' ', 'T'), IST_ZONE);
  return parsed.isValid() ? parsed.utc().toISOString() : null;
}

/** `YYYYMMDD` of the IST day of an instant (download filenames). */
export function istCompactDate(instant = Date.now()) {
  const ist = toIst(instant);
  return ist ? ist.format('YYYYMMDD') : '';
}

/** Is an expiry instant in the past relative to `nowMs`? */
export function isPast(instant, nowMs = Date.now()) {
  if (!isValidInput(instant)) return false;
  return dayjs.utc(instant).valueOf() <= nowMs;
}

export { dayjs };
